import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { CreditCard, ArrowLeft, Save, Loader2, AlertCircle, CheckCircle2, Eye, EyeOff } from 'lucide-react';
import { paymentService } from '../services/paymentService';
import { authService } from '../services/authService';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';

/**
 * Payment Method Page
 * Displays and allows updating payment method information
 */
const PaymentMethod = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [paymentMethod, setPaymentMethod] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState(null);
  const [formData, setFormData] = useState({
    payment_method_type: '',
    payment_method_details: '',
    payment_threshold: '',
  });
  const [showDetails, setShowDetails] = useState(false);

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await authService.getAccounts();
        setAccounts(response.data?.accounts || []);
        if (response.data?.accounts?.length > 0) {
          setSelectedAccountId(response.data.accounts[0].id);
        }
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);

  // Fetch payment method when account changes
  useEffect(() => {
    if (selectedAccountId) {
      fetchPaymentMethod();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId]);

  const fetchPaymentMethod = async () => {
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const response = await paymentService.getPaymentMethod({
        account_id: selectedAccountId
      });
      
      const data = response.data || {};
      setPaymentMethod(data);
      
      // Populate form with existing data
      setFormData({
        payment_method_type: data.payment_method_type || '',
        payment_method_details: data.payment_method_details ? JSON.stringify(data.payment_method_details, null, 2) : '',
        payment_threshold: data.payment_threshold || '',
      });
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load payment method';
      setError(errorMessage);
      console.error('Payment method fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
    setSuccess(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);

      // Parse payment_method_details if it's a JSON string
      let paymentMethodDetails = formData.payment_method_details;
      if (paymentMethodDetails) {
        try {
          paymentMethodDetails = JSON.parse(paymentMethodDetails);
        } catch (e) {
          // If not valid JSON, treat as plain string
          paymentMethodDetails = formData.payment_method_details;
        }
      }

      const updateData = {
        account_id: selectedAccountId,
        payment_method_type: formData.payment_method_type,
        payment_method_details: paymentMethodDetails,
        payment_threshold: formData.payment_threshold ? parseFloat(formData.payment_threshold) : null,
      };

      await paymentService.updatePaymentMethod(updateData);
      
      setSuccess('Payment method updated successfully!');
      // Refresh payment method data
      await fetchPaymentMethod();
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to update payment method';
      setError(errorMessage);
      console.error('Update error:', err);
    } finally {
      setSaving(false);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return '-';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Get payment method badge
  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      'PayPal': { variant: 'default', label: 'PayPal' },
      'Wire Transfer': { variant: 'secondary', label: 'Wire Transfer' },
      'eCheck': { variant: 'outline', label: 'eCheck' },
    };

    const config = methodConfig[method] || { variant: 'outline', label: method || 'Not Set' };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Mask sensitive details
  const maskDetails = (details) => {
    if (!details) return '';
    if (typeof details === 'string') {
      try {
        details = JSON.parse(details);
      } catch (e) {
        return details.replace(/./g, '*');
      }
    }
    if (typeof details === 'object') {
      const masked = { ...details };
      Object.keys(masked).forEach(key => {
        if (typeof masked[key] === 'string' && masked[key].length > 4) {
          masked[key] = masked[key].substring(0, 4) + '****';
        }
      });
      return JSON.stringify(masked, null, 2);
    }
    return String(details);
  };

  if (loading && !paymentMethod) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading payment method...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      {/* Header */}
      <div className="mb-2 sm:mb-3 lg:mb-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button asChild variant="outline" size="sm" className="text-xs">
            <Link to="/finance/invoices">
              <ArrowLeft className="w-3 h-3 mr-1.5" />
              Back
            </Link>
          </Button>
          <div>
            <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Payment Method</h1>
            <p className="text-muted-foreground text-xs">Manage your payment method settings</p>
          </div>
        </div>
      </div>

      {/* Error message */}
      {error && (
        <Alert variant="destructive" className="mb-2 sm:mb-3 lg:mb-4">
          <AlertCircle className="h-4 w-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* Success message */}
      {success && (
        <Alert className="mb-2 sm:mb-3 lg:mb-4 border-green-500 bg-green-500/10 dark:bg-green-500/20">
          <CheckCircle2 className="h-4 w-4 text-green-600 dark:text-green-400" />
          <AlertDescription className="text-green-800 dark:text-green-200">{success}</AlertDescription>
        </Alert>
      )}

      {/* Account Selection */}
      {accounts.length > 1 && (
        <Card className="mb-2 sm:mb-3 lg:mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm sm:text-base">Select Account</CardTitle>
          </CardHeader>
          <CardContent>
            <Select
              value={selectedAccountId?.toString()}
              onValueChange={(value) => setSelectedAccountId(parseInt(value))}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select an account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.map(account => (
                  <SelectItem key={account.id} value={account.id.toString()}>
                    {account.account_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>
      )}

      {/* Current Payment Method */}
      {paymentMethod && (
        <Card className="mb-2 sm:mb-3 lg:mb-4">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
              <CreditCard className="h-4 w-4" />
              Current Payment Method
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Payment Method</Label>
                <div className="mt-1">
                  {getPaymentMethodBadge(paymentMethod.payment_method_type)}
                </div>
              </div>
              <div>
                <Label className="text-sm font-medium text-muted-foreground">Payment Threshold</Label>
                <p className="text-lg font-semibold mt-1">
                  {formatCurrency(paymentMethod.payment_threshold)}
                </p>
              </div>
              {paymentMethod.updated_at && (
                <div>
                  <Label className="text-sm font-medium text-muted-foreground">Last Updated</Label>
                <p className="text-sm mt-1">
                    {new Date(paymentMethod.updated_at).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
            {paymentMethod.payment_method_details && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-sm font-medium text-muted-foreground">Payment Details</Label>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setShowDetails(!showDetails)}
                  >
                    {showDetails ? (
                      <>
                        <EyeOff className="w-4 h-4 mr-1" />
                        Hide
                      </>
                    ) : (
                      <>
                        <Eye className="w-4 h-4 mr-1" />
                        Show
                      </>
                    )}
                  </Button>
                </div>
                <div className="p-3 bg-muted rounded-md">
                  <pre className="text-xs overflow-auto">
                    {showDetails
                      ? JSON.stringify(paymentMethod.payment_method_details, null, 2)
                      : maskDetails(JSON.stringify(paymentMethod.payment_method_details, null, 2))}
                  </pre>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Update Payment Method Form */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm sm:text-base">Update Payment Method</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="payment_method_type">Payment Method Type *</Label>
              <Select
                value={formData.payment_method_type}
                onValueChange={(value) => handleInputChange('payment_method_type', value)}
                required
              >
                <SelectTrigger id="payment_method_type">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PayPal">PayPal</SelectItem>
                  <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                  <SelectItem value="eCheck">eCheck</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="payment_method_details">Payment Method Details</Label>
              <textarea
                id="payment_method_details"
                className="w-full mt-1.5 min-h-[120px] px-3 py-2 text-sm border rounded-md resize-none font-mono"
                placeholder='Enter payment details as JSON, e.g., {"account": "123456", "routing": "987654"}'
                value={formData.payment_method_details}
                onChange={(e) => handleInputChange('payment_method_details', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Enter payment method details as JSON format
              </p>
            </div>

            <div>
              <Label htmlFor="payment_threshold">Payment Threshold ($)</Label>
              <Input
                id="payment_threshold"
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={formData.payment_threshold}
                onChange={(e) => handleInputChange('payment_threshold', e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">
                Minimum amount before payment is processed
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/finance/invoices')}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={saving || !formData.payment_method_type}
              >
                {saving ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Save Changes
                  </>
                )}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentMethod;
